import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { NotificationCategory, NotificationPreferenceDto } from '@roadfootage/api-client'
import { useApi } from '../../composables/api'

export type SetEnabledResult = { ok: true } | { ok: false; message: string }
export type SetEmailEnabledResult = SetEnabledResult

type Channel = 'emailEnabled' | 'inAppEnabled'

const SAVE_ERROR = 'Could not save your notification setting. Please try again.'

export const useNotificationPreferencesStore = defineStore('notificationPreferences', () => {
  const { meApi } = useApi()

  const preferences = ref<NotificationPreferenceDto[]>([])
  const loading = ref(false)
  const loadFailed = ref(false)

  async function load() {
    loading.value = true
    loadFailed.value = false
    try {
      const response = await meApi.meGetNotificationPreferences()
      preferences.value = response.preferences ?? []
    } catch (error) {
      console.error('Failed to load notification preferences', error)
      loadFailed.value = true
    } finally {
      loading.value = false
    }
  }

  // Optimistic: the toggle flips at once, only the changed category and channel are sent, and a failure puts it back.
  async function save(
    category: NotificationCategory,
    channel: Channel,
    enabled: boolean
  ): Promise<SetEnabledResult> {
    const row = preferences.value.find((p) => p.category === category)
    if (!row) return { ok: false, message: 'Unknown notification category.' }
    if (row.isTransactional) return { ok: false, message: 'This notification is always sent.' }
    if (channel === 'inAppEnabled' && !row.inAppAvailable) {
      return { ok: false, message: 'This notification is not available in the app.' }
    }

    const previous = row[channel]
    row[channel] = enabled
    try {
      const response = await meApi.meSetNotificationPreferences({
        updateNotificationPreferencesRequest: { preferences: [{ category, [channel]: enabled }] }
      })
      preferences.value = response.preferences ?? preferences.value
      return { ok: true }
    } catch (error) {
      console.error('Failed to save notification preference', error)
      // Re-find the row: a load may have replaced the list while the PUT was in flight.
      const current = preferences.value.find((p) => p.category === category)
      if (current) current[channel] = previous
      return { ok: false, message: SAVE_ERROR }
    }
  }

  const setEmailEnabled = (category: NotificationCategory, enabled: boolean) =>
    save(category, 'emailEnabled', enabled)
  const setInAppEnabled = (category: NotificationCategory, enabled: boolean) =>
    save(category, 'inAppEnabled', enabled)

  return { preferences, loading, loadFailed, load, setEmailEnabled, setInAppEnabled }
})
